import React, { FC, useMemo } from 'react'
import styled from 'styled-components'
import { CrossRecurrence as CrossData } from '../../hooks/event-comparison'
import { SERIES_BASE, SERIES_COMPARE } from './styles'

const INK = '#212121'
const MUTED = '#555555'
const FAINT = '#B0B0B0'
const BORDER = '#EAECF0'
const SURFACE = '#F9F9F9'

const SectionHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin: 24px 0 12px;
`
const Kicker = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: ${FAINT};
  text-transform: uppercase;
`
const Name = styled.h2`
  font-size: 15px;
  font-weight: 680;
  color: ${INK};
  margin: 0;
`
const Note = styled.span`
  margin-left: auto;
  font-size: 11.5px;
  color: ${FAINT};
`
const Card = styled.div`
  background: #fff;
  border: 1px solid ${BORDER};
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06);
  padding: 20px;
`
const CardTitle = styled.div`
  font-size: 14.5px;
  font-weight: 660;
  color: ${INK};
`
const CardSub = styled.div`
  font-size: 11.5px;
  color: ${FAINT};
  margin: 2px 0 18px;
`
const Empty = styled.div`
  font-size: 12.5px;
  color: ${FAINT};
  padding: 8px 0;
`

/* topo: número grande de clientes em comum */
const Hero = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 4px;
  padding: 6px 0 18px;
`
const HeroNum = styled.div`
  font-size: 40px;
  font-weight: 760;
  color: ${INK};
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
  line-height: 1;
`
const HeroCap = styled.div`
  font-size: 12.5px;
  color: ${MUTED};
`

/* barra de proporção (overlap dentro de cada lado) */
const DirRow = styled.div`
  padding: 12px 0;
  & + & {
    border-top: 1px solid #f2f4f7;
  }
`
const DirTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 7px;
`
const DirText = styled.span`
  font-size: 12.5px;
  color: ${INK};
  b {
    font-weight: 660;
  }
`
const DirPct = styled.span<{ $c: string }>`
  font-size: 16px;
  font-weight: 720;
  color: ${(p) => p.$c};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`
const Track = styled.div`
  height: 10px;
  border-radius: 6px;
  background: ${SURFACE};
  overflow: hidden;
`
const Fill = styled.div<{ $w: number; $c: string }>`
  height: 100%;
  border-radius: 6px;
  background: ${(p) => p.$c};
  width: ${(p) => Math.min(Math.max(p.$w, 0), 100)}%;
`
const Foot = styled.div`
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
  margin-top: 16px;
  font-size: 11.5px;
  color: ${MUTED};
`
const FootItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  b {
    color: ${INK};
    font-weight: 640;
    font-variant-numeric: tabular-nums;
  }
`
const Dot = styled.span<{ $c: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => p.$c};
  flex: none;
`

const fmtInt = (n: number) => Math.round(n).toLocaleString('pt-BR')
const pct = (part: number, total: number) => (total > 0 ? (part / total) * 100 : 0)

interface Props {
  baseName: string
  compareName: string
  data: CrossData
  excludeCourtesy: boolean
}

/**
 * Card de recorrência CRUZADA: clientes (por CPF) que compraram os DOIS eventos
 * comparados. Mostra o total em comum + a taxa de recompra em cada direção.
 */
const CrossRecurrence: FC<Props> = ({ baseName, compareName, data, excludeCourtesy }) => {
  const view = useMemo(() => {
    const key = excludeCourtesy ? 'clientesPagantes' : 'clientes'
    const baseTotal = data.base?.[key] ?? 0
    const compareTotal = data.compare?.[key] ?? 0
    const both = data.overlap?.[key] ?? 0
    return { baseTotal, compareTotal, both }
  }, [data, excludeCourtesy])

  const hasData = !!(data.base && data.compare && data.overlap)

  return (
    <>
      <SectionHead>
        <Kicker>Fidelização</Kicker>
        <Name>Recorrência entre eventos</Name>
        <Note>{excludeCourtesy ? 'só clientes pagantes' : 'inclui cortesias'} · por CPF</Note>
      </SectionHead>

      <Card>
        <CardTitle>Clientes que compraram os dois</CardTitle>
        <CardSub>
          Interseção de compradores de <b>{baseName}</b> e <b>{compareName}</b> — identidade por CPF
        </CardSub>

        {!hasData ? (
          <Empty>Sem dados de recorrência para os eventos selecionados.</Empty>
        ) : (
          <>
            <Hero>
              <HeroNum>{fmtInt(view.both)}</HeroNum>
              <HeroCap>clientes compraram os dois eventos</HeroCap>
            </Hero>

            <DirRow>
              <DirTop>
                <DirText>
                  dos clientes de <b>{compareName}</b> também compraram <b>{baseName}</b>
                </DirText>
                <DirPct $c={SERIES_COMPARE}>{pct(view.both, view.compareTotal).toFixed(0)}%</DirPct>
              </DirTop>
              <Track>
                <Fill $w={pct(view.both, view.compareTotal)} $c={SERIES_COMPARE} />
              </Track>
            </DirRow>

            <DirRow>
              <DirTop>
                <DirText>
                  dos clientes de <b>{baseName}</b> voltaram para <b>{compareName}</b>
                </DirText>
                <DirPct $c={SERIES_BASE}>{pct(view.both, view.baseTotal).toFixed(0)}%</DirPct>
              </DirTop>
              <Track>
                <Fill $w={pct(view.both, view.baseTotal)} $c={SERIES_BASE} />
              </Track>
            </DirRow>

            <Foot>
              <FootItem>
                <Dot $c={SERIES_BASE} />
                {baseName}: <b>{fmtInt(view.baseTotal)}</b> clientes
              </FootItem>
              <FootItem>
                <Dot $c={SERIES_COMPARE} />
                {compareName}: <b>{fmtInt(view.compareTotal)}</b> clientes
              </FootItem>
            </Foot>
          </>
        )}
      </Card>
    </>
  )
}

export default CrossRecurrence
